import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useSearchParams,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "hooks/useAuth";
import AuthPageShell from "components/auth/AuthPageShell";
import {
  DEVICE_LINK_POLL_MS,
  approveDeviceLinkSession,
  buildDeviceLinkUrl,
  claimDeviceLinkSession,
  createDeviceLinkSession,
  signInWithDeviceLinkToken,
  takePrefetchedDeviceLinkSession,
} from "services/deviceLinkService";

/**
 * Sign in on a computer by approving it from a phone (route: /link-device).
 *
 * One route, two devices. Without `?c=` this is the computer: it shows a QR code and
 * waits. With `?c=` it is the phone that just scanned that QR, and it asks the person
 * holding it to approve. The phone side needs no scanner of its own — the QR holds an
 * ordinary https URL, so the native camera opens it.
 *
 * The security model lives in functions/src/auth/deviceLink.js. What matters here:
 * `claimSecret` is held in a ref and never written to storage, because it is the only
 * thing that stops anyone who photographed the QR from collecting the token.
 */

/**
 * The same frame, heading and type as AuthPage/AuthFlow, because this is the same
 * journey.
 */
const PageShell = ({ title, subtitle, children }) => (
  <AuthPageShell>
    <Box sx={{ p: 3 }}>
      <Typography variant="h3" sx={{ mb: 1, textAlign: "center" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  </AuthPageShell>
);

/**
 * The computer. Opens a session, renders it as a QR, then polls until the phone
 * approves it or the session runs out.
 */
const DesktopView = () => {
  const navigate = useNavigate();
  // Not state: the secret must not be part of anything React might hand to a
  // devtools inspector as rendered output, and nothing here re-renders on it.
  const claimSecretRef = useRef(null);
  // StrictMode double-invokes effects in development, and a second create call would
  // leave the first session orphaned behind a QR nobody is looking at any more.
  const startedRef = useRef(false);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("loading");
  // The collected token, handed to a separate effect rather than spent inside the
  // poll. See the comment on that effect for why that separation is load-bearing.
  const [pendingToken, setPendingToken] = useState(null);
  const [error, setError] = useState(null);

  const startSession = useCallback(async ({ usePrefetched = false } = {}) => {
    setStatus("loading");
    setError(null);
    claimSecretRef.current = null;
    setSession(null);
    try {
      // Arriving from AuthPage's button, the session is already open.
      const data =
        (usePrefetched && takePrefetchedDeviceLinkSession()) ||
        (await createDeviceLinkSession());
      claimSecretRef.current = data.claimSecret;
      setSession({ sessionId: data.sessionId });
      setStatus("waiting");
    } catch (err) {
      setError(err.message || "Could not start sign-in. Please try again.");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startSession({ usePrefetched: true });
  }, [startSession]);

  useEffect(() => {
    if (status !== "waiting" || !session) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await claimDeviceLinkSession({
          sessionId: session.sessionId,
          claimSecret: claimSecretRef.current,
        });
        if (cancelled) return;
        // ⚠️ THE ONLY PLACE THE CODE IS ALLOWED TO LAPSE. This used to run off a
        // local timer against the session's `expiresAt`, which meant the desktop
        // decided on its own clock while the server decided on its. Any skew
        // between them opened a window where the desktop had stopped polling but
        // the session was still live: the phone approved, the server minted a
        // token, and nobody was listening for it. The phone showed success and the
        // computer sat there. Asking the server and believing the answer removes
        // the second clock, and with it the disagreement.
        if (data.status === "expired") {
          setStatus("lapsed");
          return;
        }
        if (data.status !== "approved") return;
        // Both of these stop the polling: `status` is in this effect's deps, so
        // changing it tears the interval down. Which is also why the sign-in itself
        // must NOT happen here — see the effect below.
        setStatus("signing-in");
        setPendingToken(data.customToken);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || "Could not finish signing in. Please try again.");
        setStatus("error");
      }
    };

    const timer = setInterval(poll, DEVICE_LINK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [status, session]);

  /**
   * Spend the token, on an effect of its own.
   *
   * ⚠️ THIS CANNOT LIVE IN THE POLL. Collecting a token means setting `status`, and
   * `status` is one of the poll effect's dependencies, so React tears that effect
   * down and runs its cleanup the instant the token arrives — synchronously, while a
   * sign-in awaited inside it would still be mid-flight. Its `cancelled` flag would
   * then be true by the time the await resolved, the navigate would be skipped, and
   * the desktop would sit on "Signing you in" forever on a session that had in fact
   * signed in. A mocked sign-in resolves in a microtask and wins that race, so the
   * bug only ever appeared against a real network.
   *
   * Keyed on the token instead, this cleanup runs only if the component actually
   * unmounts, which is the only case where abandoning the navigate is correct.
   */
  useEffect(() => {
    if (!pendingToken) return undefined;
    let unmounted = false;

    (async () => {
      try {
        await signInWithDeviceLinkToken(pendingToken);
        if (unmounted) return;
        navigate("/onboarding");
      } catch (err) {
        if (unmounted) return;
        setError(err.message || "Could not finish signing in. Please try again.");
        setStatus("error");
      }
    })();

    return () => {
      unmounted = true;
    };
  }, [pendingToken, navigate]);

  const renderBody = () => {
    if (status === "signing-in") {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Approved. Signing you in.
          </Typography>
        </Stack>
      );
    }

    if (status === "error") {
      return (
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" size="large" onClick={startSession}>
            Try again
          </Button>
        </Stack>
      );
    }

    const lapsed = status === "lapsed";

    return (
      <Stack spacing={3}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            // Hug the code rather than fill the width: the white is the quiet zone, not a band.
            width: "fit-content",
            // alignSelf, not mx: auto, which Stack resets on its children.
            alignSelf: "center",
            p: 2,
            bgcolor: "common.white",
            borderRadius: 1,
          }}
        >
          {/* Blurred rather than hidden or swapped for a message, which is what
              WhatsApp does and for a good reason: the code stays recognisably a
              code, so the page keeps its shape and the state reads as "this one is
              stale" rather than "something went wrong". The button sits on top of
              it, not underneath, so the thing you are being asked to replace and
              the way to replace it are in the same place. */}
          <Box
            sx={{
              opacity: lapsed ? 0.45 : 1,
              filter: lapsed ? "blur(4px)" : "none",
            }}
          >
            {/* While the session opens (a direct visit, or a new code), a blank
                quiet zone of the same size holds the spot: no spinner, the code
                just appears in place. */}
            {session ? (
              <QRCodeSVG value={buildDeviceLinkUrl(session.sessionId)} size={280} />
            ) : (
              <Box sx={{ width: 280, height: 280 }} />
            )}
          </Box>
          {lapsed && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button variant="contained" onClick={startSession}>
                Show a new code
              </Button>
            </Box>
          )}
        </Box>

        {/* No spinner and no "waiting for approval" line. There is nothing to wait
            for that the person cannot see for themselves: they are holding the
            phone that does the approving. WhatsApp shows a bare QR for the same
            reason. */}
        <Button component={RouterLink} to="/auth" size="large">
          Sign in with your password instead
        </Button>
      </Stack>
    );
  };

  return (
    <PageShell
      title="Sign in with your phone"
      subtitle="If you're signed in on your phone, scan this code with its camera, then tap Approve."
    >
      {renderBody()}
    </PageShell>
  );
};

/**
 * The phone. Already signed in, and now being asked to vouch for a computer.
 *
 * There is nothing to look up before showing this: a pending session belongs to
 * whoever is standing at the computer that made it, so the only question worth
 * asking is whether this person means to approve it.
 */
const ApproveView = ({ sessionId }) => {
  const { user, userLoading } = useAuth();
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState(null);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    setError(null);
    try {
      await approveDeviceLinkSession(sessionId);
      setApproved(true);
    } catch (err) {
      setError(err.message || "Could not approve that device. Please try again.");
    } finally {
      setApproving(false);
    }
  }, [sessionId]);

  const renderBody = () => {
    if (userLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // An anonymous account is a guest on a public page, not a signed-in person, and
    // the server refuses it. Say so here rather than letting them tap and fail.
    if (!user || user.isAnonymous) {
      return (
        <Stack spacing={2}>
          <Typography variant="body1">
            Please sign in on this phone first, then scan the code again.
          </Typography>
          <Button component={RouterLink} to="/auth" variant="contained" size="large">
            Sign in
          </Button>
        </Stack>
      );
    }

    if (approved) {
      return (
        <Alert severity="success">
          The other device is signed in. You can close this page.
        </Alert>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Typography variant="body1">
          The device showing this code will be signed in as{" "}
          {user.email || "you"}.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <LoadingButton
          variant="contained"
          size="large"
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </LoadingButton>
        {/* "Not now" offered a later that does not exist. This is the refusal.
            It notifies nothing: leaving without approving is what denies the
            request, and the unapproved code lapses on its own two minutes later,
            at which point the other screen quietly replaces it. */}
        <Button component={RouterLink} to="/dashboard" variant="text" size="small">
          Don't allow
        </Button>
      </Stack>
    );
  };

  return <PageShell title="Approve this sign-in">{renderBody()}</PageShell>;
};

const LinkDevicePage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = (searchParams.get("c") || "").trim();
  return sessionId ? <ApproveView sessionId={sessionId} /> : <DesktopView />;
};

export default LinkDevicePage;
